'use client'

import { useState } from 'react'
import Link from 'next/link'

// Client-side translation lookup (kept lean — only contact section)
const TX = {
    en: {
        heroLabel: 'Contact',
        heroTitle: 'Get in Touch',
        heroSubtitle: "Have questions or suggestions? We're always happy to help and hear your feedback",
        howToReach: 'How to Reach Us',
        contactInfo: 'Contact Information',
        contactInfoSubtitle: "Choose a convenient way to reach us — we'll respond as soon as possible",
        emailDescription: 'For business inquiries and partnerships',
        telegramDescription: 'Quick replies via messenger',
        responseTime: 'We typically respond ',
        responseTimeBold: 'within 24 hours',
        blogTitle: 'Read Our Blog',
        blogSubtitle: 'Tips, guides, and answers to common questions',
        blogLink: 'Go to Blog',
        formLabel: 'Contact Form',
        formTitle: 'Send us a Message',
        successTitle: 'Message Sent!',
        successMessage: "Thank you for reaching out. We'll get back to you soon.",
        sendAnother: 'Send Another',
        nameLabel: 'Your Name',
        namePlaceholder: "What's your name?",
        emailLabel: 'Email',
        subjectLabel: 'Subject',
        subjectPlaceholder: 'What would you like to discuss?',
        messageLabel: 'Message',
        messagePlaceholder: 'Tell us more...',
        sendButton: 'Send Message',
        sendingButton: 'Sending...',
        networkError: 'Network error. Please try again later.',
        genericError: 'An error occurred while sending',
        languagesLabel: 'Languages',
        languagesValue: 'English · Русский',
        privacyNoteBefore: 'By clicking "Send", you agree to the processing of your data in accordance with the ',
        privacyNoteLink: 'privacy policy',
        privacyNoteAfter: '.',
        showTelegram: true,
    },
    ru: {
        heroLabel: 'Контакты',
        heroTitle: 'Связаться с нами',
        heroSubtitle: 'Есть вопросы или предложения? Мы всегда рады помочь и услышать вашу обратную связь',
        howToReach: 'Как нас найти',
        contactInfo: 'Контактная информация',
        contactInfoSubtitle: 'Выберите удобный способ связи — мы ответим в кратчайшие сроки',
        emailDescription: 'Для деловых запросов и сотрудничества',
        telegramDescription: 'Быстрые ответы в мессенджере',
        responseTime: 'Обычно отвечаем ',
        responseTimeBold: 'в течение 24 часов',
        blogTitle: 'Читайте наш блог',
        blogSubtitle: 'Советы, гайды и ответы на вопросы',
        blogLink: 'Перейти в блог',
        formLabel: 'Форма обратной связи',
        formTitle: 'Напишите нам',
        successTitle: 'Сообщение отправлено!',
        successMessage: 'Спасибо за ваше обращение. Мы свяжемся с вами в ближайшее время.',
        sendAnother: 'Отправить ещё',
        nameLabel: 'Ваше имя',
        namePlaceholder: 'Как вас зовут?',
        emailLabel: 'Email',
        subjectLabel: 'Тема',
        subjectPlaceholder: 'О чём хотите написать?',
        messageLabel: 'Сообщение',
        messagePlaceholder: 'Расскажите подробнее...',
        sendButton: 'Отправить сообщение',
        sendingButton: 'Отправка...',
        networkError: 'Ошибка сети. Попробуйте позже.',
        genericError: 'Произошла ошибка при отправке',
        languagesLabel: 'Языки',
        languagesValue: 'Русский · English',
        privacyNoteBefore: 'Нажимая «Отправить», вы соглашаетесь с обработкой данных согласно ',
        privacyNoteLink: 'политике конфиденциальности',
        privacyNoteAfter: '.',
        showTelegram: true,
    },
}

export default function ContactPageClient({ lang = 'en' }) {
    const tx = TX[lang] || TX.en

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    })
    const [status, setStatus] = useState('idle')
    const [errorMessage, setErrorMessage] = useState('')

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setStatus('loading')
        setErrorMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            })

            if (response.ok) {
                setStatus('success')
                setFormData({ name: '', email: '', subject: '', message: '' })
            } else {
                const data = await response.json()
                setErrorMessage(data.error || tx.genericError)
                setStatus('error')
            }
        } catch {
            setErrorMessage(tx.networkError)
            setStatus('error')
        }
    }

    const contactMethods = [
        {
            key: 'email',
            label: 'Email',
            value: 'info@triplinkhub.com',
            href: 'mailto:info@triplinkhub.com',
            description: tx.emailDescription,
        },
        ...(tx.showTelegram ? [{
            key: 'telegram',
            label: 'Telegram',
            value: '@triplinkhub',
            href: 'https://t.me/triplinkhub',
            description: tx.telegramDescription,
        }] : []),
    ]

    const socials = [
        { label: 'Telegram', href: 'https://t.me/triplinkhub' },
    ]

    return (
        <main className="gf-root">
            {/* ── MAST ──────────────────────────────────────── */}
            <div className="doc-mast">
                <p className="doc-kick">{tx.heroLabel}</p>
                <h1>{tx.heroTitle}</h1>
                <div className="doc-meta">
                    <span>{tx.heroSubtitle}</span>
                    <span><b>{tx.languagesLabel}</b> {tx.languagesValue}</span>
                </div>
            </div>

            {/* ── GRID ──────────────────────────────────────── */}
            <div className="ct2-grid">
                {/* Channels column */}
                <div className="ct2-ch">
                    {contactMethods.map((method) => (
                        <a
                            key={method.key}
                            className="row"
                            href={method.href}
                            target={method.href.startsWith('http') ? '_blank' : undefined}
                            rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                            <span className="k">{method.label}</span>
                            <span className="v">{method.value}</span>
                            {method.description && <span className="sub">{method.description}</span>}
                        </a>
                    ))}

                    <Link className="row" href={`/${lang}/blog`}>
                        <span className="k">{tx.blogTitle}</span>
                        <span className="v">{tx.blogLink}</span>
                        <span className="sub">{tx.blogSubtitle}</span>
                    </Link>

                    {tx.responseTime && (
                        <div className="row">
                            <span className="k">{tx.howToReach}</span>
                            <span className="sub">
                                {tx.responseTime}
                                <b>{tx.responseTimeBold}</b>
                            </span>
                        </div>
                    )}

                    {tx.showTelegram && (
                        <div className="ct2-soc">
                            {socials.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {s.label}
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                {/* Form column */}
                {status === 'success' ? (
                    <div className="ct2-form">
                        <div className="fh">{tx.formLabel}</div>
                        <h2
                            style={{
                                fontFamily: 'var(--sans)',
                                fontSize: '24px',
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                color: 'var(--ink)',
                                margin: '0 0 12px',
                            }}
                        >
                            {tx.successTitle}
                        </h2>
                        <p className="note" style={{ marginTop: 0, fontSize: '14px' }}>
                            {tx.successMessage}
                        </p>
                        <button
                            type="button"
                            className="submit"
                            style={{ marginTop: '18px' }}
                            onClick={() => setStatus('idle')}
                        >
                            {tx.sendAnother}
                        </button>
                    </div>
                ) : (
                    <form className="ct2-form" onSubmit={handleSubmit}>
                        <div className="fh">{tx.formTitle}</div>

                        {status === 'error' && (
                            <p
                                id="contact-form-error"
                                role="alert"
                                aria-live="assertive"
                                className="note"
                                style={{ marginTop: 0, marginBottom: '18px', color: '#b42318' }}
                            >
                                {errorMessage}
                            </p>
                        )}

                        <div className="field">
                            <label htmlFor="name">{tx.nameLabel}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                aria-invalid={status === 'error' ? 'true' : undefined}
                                aria-describedby={status === 'error' ? 'contact-form-error' : undefined}
                                placeholder={tx.namePlaceholder}
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="email">{tx.emailLabel}</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                aria-invalid={status === 'error' ? 'true' : undefined}
                                aria-describedby={status === 'error' ? 'contact-form-error' : undefined}
                                placeholder="your@email.com"
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="subject">{tx.subjectLabel}</label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder={tx.subjectPlaceholder}
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="message">{tx.messageLabel}</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                aria-required="true"
                                aria-invalid={status === 'error' ? 'true' : undefined}
                                aria-describedby={status === 'error' ? 'contact-form-error' : undefined}
                                rows={6}
                                placeholder={tx.messagePlaceholder}
                            />
                        </div>

                        <button type="submit" className="submit" disabled={status === 'loading'}>
                            {status === 'loading' ? tx.sendingButton : tx.sendButton}
                        </button>

                        <p className="note">
                            {tx.privacyNoteBefore}
                            <Link href={`/${lang}/privacy`} style={{ color: 'var(--pine)' }}>
                                {tx.privacyNoteLink}
                            </Link>
                            {tx.privacyNoteAfter}
                        </p>
                    </form>
                )}
            </div>
        </main>
    )
}
